import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, Upload, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent } from '../../../components/ui/Card';
import { useDocuments } from '../../../core/hooks/useDocuments';
import { useStorage } from '../../../core/hooks/useStorage';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useClient } from '../../../core/hooks/useClient';
import { useLinkedClientAccount } from '../../../core/hooks/useLinkedClientAccount';
import { resolveProjectContactEmail } from '../../../core/utils/projectContact';
import { DocumentStatus, Document, DocumentSignature, DocumentSignatureStatus } from '../../../core/models/Document';
import { EmptyState } from '../../../components/shared/EmptyState';
import { LoadingState } from '../../../components/shared/LoadingState';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { DocumentPreviewDialog } from '../../documents/components/DocumentPreviewDialog';
import { SignatureRequestDialog } from '../../documents/components/SignatureRequestDialog';
import { SignatureCanvas } from '../../documents/components/SignatureCanvas';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { ErrorDialog } from '../../../components/shared/ErrorDialog';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '../../../core/services/firebase';
import { logError, logDebug } from '../../../lib/utils';

interface ClientDocumentsTabProps {
  clientId: string;
}

export const ClientDocumentsTab: React.FC<ClientDocumentsTabProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();
  const { documents, loading, deleteDocument, sendSigningLink, submitOrganizationDocumentSignature, updateDocument, generateDocumentPdf } = useDocuments(clientId);
  const { uploadFile, uploading, progress } = useStorage();
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [signatureRequestOpen, setSignatureRequestOpen] = useState(false);
  const [signatureCanvasOpen, setSignatureCanvasOpen] = useState(false);
  const [copyingLink, setCopyingLink] = useState(false);
  const [sharingLink, setSharingLink] = useState(false);
  const [signing, setSigning] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);
  const { client } = useClient(selectedDocument?.clientId || null);
  const linkedClientId = client?.clientId || client?.organizationId || null;
  const { clientAccount } = useLinkedClientAccount(linkedClientId);
  const contactEmail = resolveProjectContactEmail(client, clientAccount);

  // Keep selectedDocument in sync with documents array updates
  useEffect(() => {
    if (selectedDocument) {
      const updatedDoc = documents.find(d => d.id === selectedDocument.id);
      if (updatedDoc) {
        setSelectedDocument(updatedDoc);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on id only; full selectedDocument causes replace loops
  }, [documents, selectedDocument?.id]);

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case DocumentStatus.COMPLETED:
        return 'success';
      case DocumentStatus.PENDING_SIGNATURES:
        return 'warning';
      case DocumentStatus.CANCELLED:
      case DocumentStatus.EXPIRED:
        return 'error';
      default:
        return 'default';
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      const path = `clients/${clientId}/documents/${Date.now()}_${file.name}`;
      const downloadURL = await uploadFile(file, path);
      console.log('File uploaded:', downloadURL);
      // TODO: Create document record in Firestore
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleViewDocument = (doc: Document) => {
    setSelectedDocument(doc);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedDocument(null);
  };

  const handleRequestSignatures = async (signatures: DocumentSignature[]) => {
    if (!selectedDocument) return;

    try {
      await updateDocument(selectedDocument.id, {
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
      });

      setSelectedDocument({
        ...selectedDocument,
        signatures,
        status: DocumentStatus.PENDING_SIGNATURES,
      });

      setSignatureRequestOpen(false);
    } catch (err) {
      logError('documents', err, { source: 'ClientDocumentsTab.handleRequestSignatures', documentId: selectedDocument.id });
      alert('Failed to request signatures. Please try again.');
    }
  };

  const handleSign = async (signatureData?: string) => {
    if (!selectedDocument || !currentUser) return;

    setSigning(true);
    try {
      const result = await submitOrganizationDocumentSignature(
        selectedDocument.id,
        signatureData ? 'draw' : 'type',
        signatureData,
        signatureData ? undefined : (currentUser.displayName || currentUser.email || 'Client User'),
        new Date().toISOString()
      );

      // Refresh the document from the list
      const updatedDoc = documents.find(d => d.id === selectedDocument.id);
      if (updatedDoc) {
        setSelectedDocument(updatedDoc);
      }

      setSignatureCanvasOpen(false);

      if (result.signedPdfUrl) {
        logDebug('documents', 'Signed PDF generated', { signedPdfUrl: result.signedPdfUrl });
      }
    } catch (err: any) {
      logError('documents', err, { source: 'ClientDocumentsTab.handleSign', documentId: selectedDocument?.id });
      setErrorDialog({ open: true, message: err.message || 'Failed to sign document. Please try again.' });
    } finally {
      setSigning(false);
    }
  };

  const handleSendSigningLink = async () => {
    if (!selectedDocument || !contactEmail) {
      setErrorDialog({ open: true, message: 'Client email is required to generate a signing link. Add an email on the linked client account.' });
      return;
    }

    setCopyingLink(true);
    try {
      const result = await sendSigningLink(selectedDocument.id, contactEmail, false);
      await navigator.clipboard.writeText(result.signingLink);
      alert('Signing link copied to clipboard!');
    } catch (err: any) {
      logError('documents', err, { source: 'ClientDocumentsTab.handleSendSigningLink', documentId: selectedDocument.id });
      setErrorDialog({
        open: true,
        message: err.message || 'Failed to generate signing link. Please try again.'
      });
    } finally {
      setCopyingLink(false);
    }
  };

  const handleShareLink = async () => {
    if (!selectedDocument || !contactEmail) {
      setErrorDialog({ open: true, message: 'Client email is required to generate a sharing link. Add an email on the linked client account.' });
      return;
    }

    setSharingLink(true);
    try {
      // Use the signing link for sharing - it will show download option if no signatures required
      // and show sign button if signatures are required
      const result = await sendSigningLink(selectedDocument.id, contactEmail, false);
      
      if (result.signingLink) {
        await navigator.clipboard.writeText(result.signingLink);
        alert('Document link copied to clipboard! This link allows viewing and downloading the document. If signatures are required, the sign button will appear.');
      } else {
        setErrorDialog({
          open: true,
          message: 'Failed to generate document link. Please try again.'
        });
      }
    } catch (err: any) {
      logError('documents', err, { source: 'ClientDocumentsTab.handleShareLink', documentId: selectedDocument.id });
      setErrorDialog({
        open: true,
        message: err.message || 'Failed to copy link. Please try again.'
      });
    } finally {
      setSharingLink(false);
    }
  };

  const openDeleteDialog = (doc: Document) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteFromModal = () => {
    if (selectedDocument) {
      setDocumentToDelete(selectedDocument);
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!documentToDelete) return;
    try {
      setDeleting(true);
      await deleteDocument(documentToDelete.id, documentToDelete.clientId);
      setDeleteDialogOpen(false);
      if (selectedDocument?.id === documentToDelete.id) {
        setPreviewOpen(false);
        setSelectedDocument(null);
      }
      setDocumentToDelete(null);
    } catch (error) {
      logError('documents', error, { source: 'ClientDocumentsTab.handleDeleteConfirmed', documentId: documentToDelete.id });
      alert('Failed to delete document. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadDocument = async (doc: Document, event?: React.MouseEvent) => {
    // Prevent event propagation to avoid opening the modal
    if (event) {
      event.stopPropagation();
    }

    setDownloadingPdf(doc.id);
    try {
      // First, try to use signed PDF if available
      if (doc.signedPdfUrl) {
        const link = document.createElement('a');
        link.href = doc.signedPdfUrl;
        link.download = `${doc.title || 'document'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setDownloadingPdf(null);
        return;
      }

      // Try to get signed PDF from storage for completed documents
      if (doc.status === DocumentStatus.COMPLETED) {
        // Try common storage paths for signed documents
        const possiblePaths = [
          `documents/${doc.id}/signed.pdf`,
          `clients/${doc.clientId}/documents/${doc.id}/signed.pdf`,
          `clients/${doc.clientId}/documents/signed/${doc.id}.pdf`,
        ];

        for (const storagePath of possiblePaths) {
          try {
            const storageRef = ref(storage, storagePath);
            const downloadURL = await getDownloadURL(storageRef);
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = `${doc.title || 'document'}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadingPdf(null);
            return;
          } catch (err) {
            // Path doesn't exist, try next one
            continue;
          }
        }
      }

      // If no signed PDF found, generate PDF from HTML content
      if (doc.content) {
        try {
          const result = await generateDocumentPdf(doc.id);
          if (result.pdfUrl) {
            const link = document.createElement('a');
            link.href = result.pdfUrl;
            link.download = `${doc.title || 'document'}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadingPdf(null);
            return;
          }
        } catch (pdfError: any) {
          logError('documents', pdfError, { source: 'ClientDocumentsTab.handleDownloadDocument.generatePdf', documentId: doc.id });
          alert(pdfError.message || 'Failed to generate PDF. Please try again.');
          setDownloadingPdf(null);
          return;
        }
      }

      // Fallback: navigate to document detail page
      navigate(`/documents/${doc.id}`);
    } catch (error) {
      logError('documents', error, { source: 'ClientDocumentsTab.handleDownloadDocument', documentId: doc.id });
      console.error('Error downloading document:', error);
      // Fallback to navigating to document detail page
      navigate(`/documents/${doc.id}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading documents..." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Documents</h3>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload Document
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
          />
        </div>
      </div>

      {uploading && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Uploading...</p>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{Math.round(progress)}%</p>
            </div>
          </CardContent>
        </Card>
      )}

      {documents.length === 0 ? (
        <EmptyState
          title="No documents found"
          description="Upload documents or create new contracts for this project"
          action={{
            label: 'Upload Document',
            onClick: () => fileInputRef.current?.click(),
          }}
        />
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">{doc.title}</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {doc.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={getStatusColor(doc.status)}>
                        {doc.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Version {doc.version}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        • {new Date(doc.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewDocument(doc)}
                      title="View document"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => handleDownloadDocument(doc, e)}
                      title="Download document"
                      disabled={downloadingPdf === doc.id}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(doc)}
                      title="Delete document"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {/* Document Preview Modal */}
      {selectedDocument && (
        <DocumentPreviewDialog
          open={previewOpen}
          onClose={handleClosePreview}
          document={selectedDocument}
          onRequestSignatures={() => setSignatureRequestOpen(true)}
          onSign={() => setSignatureCanvasOpen(true)}
          onBackToDocuments={() => navigate('/documents')}
          onCopySigningLink={handleSendSigningLink}
          onShareLink={handleShareLink}
          onDelete={handleDeleteFromModal}
          copyingLink={copyingLink}
          sharingLink={sharingLink}
          client={client}
        />
      )}

      {/* Signature Request Dialog */}
      {selectedDocument && (
        <SignatureRequestDialog
          open={signatureRequestOpen}
          onClose={() => setSignatureRequestOpen(false)}
          onSubmit={handleRequestSignatures}
          documentTitle={selectedDocument.title}
        />
      )}

      {/* Signature Canvas Dialog */}
      {selectedDocument && (
        <Dialog open={signatureCanvasOpen} onOpenChange={(open) => !signing && setSignatureCanvasOpen(open)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Sign Document</DialogTitle>
            </DialogHeader>
            {signing ? (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-sm text-gray-600">Signing document and generating PDF...</p>
              </div>
            ) : (
              <SignatureCanvas
                onSave={handleSign}
                onCancel={() => setSignatureCanvasOpen(false)}
                userName={currentUser?.displayName || currentUser?.email || undefined}
                userTitle={selectedDocument.signatures.find(
                  (sig) => sig.userId === currentUser?.uid && sig.status === DocumentSignatureStatus.PENDING
                )?.title}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <ErrorDialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: '' })}
        message={errorDialog.message}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteDialogOpen(false);
          setDocumentToDelete(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Delete document"
        description={`Are you sure you want to permanently delete "${documentToDelete?.title || 'this document'}"? This will also remove any related PDFs and signature files.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={deleting}
      />
    </div>
  );
};
